import { CircularProgress } from '@mui/joy'
import dayjs from 'dayjs'
import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ExclamationIcon from '@/assets/exclamation-icon.png'
import IconLogo from '@/assets/icon-white.png'
import {
  Button,
  CheckboxInput,
  Column,
  Form,
  Modal,
  ProgressBar,
  Row,
  Text,
  TextInput
} from '@/components/core'
import { useAuth } from '@/contexts/AuthContext.tsx'
import saveOnboardingQuestions from '@/features/Onboarding/services/saveOnboardingQuestions.ts'
import { classNameBuilder } from '@/helpers'
import { required } from '@/helpers/formRules'
import { useForm } from '@/hooks/useForm'
import { ROUTES } from '@/types/routes.ts'

type StepsProps = {
  active: number
}

type Step = {
  title: string
  stepTitle: string
}

const steps: Step[] = [
  { title: 'Verificar dados', stepTitle: 'Verificação de dados' },
  { title: 'Queremos te conhecer mais', stepTitle: 'Sobre o aluno' }
]

const Steps: React.FC<StepsProps> = ({ active }: StepsProps) => {
  return (
    <Row className="gap-4 md:flex-col md:gap-1">
      {steps.map((step, index) => (
        <Row
          key={index}
          className={`gap-2 opacity-${
            index < active || index > active ? '50' : '100'
          }`}
        >
          <Row className="items-center gap-1 md:flex-col">
            <div
              className={classNameBuilder(
                'h-3 w-3 rounded-full border-2 md:h-4 md:w-4',

                index < active || index === active
                  ? 'border-[#e6d7ff] bg-[#e6d7ff]'
                  : '',
                index > active ? 'border-[#e6d7ff] bg-transparent' : ''
              )}
            ></div>
            {index < steps.length - 1 && (
              <div
                className={classNameBuilder(
                  'h-0 w-px bg-[#e6d7ff] md:h-6',
                  index === active || index > active ? 'opacity-25' : '',
                  index < active ? 'opacity-100' : ''
                )}
              ></div>
            )}
          </Row>
          <Column className="h-4">
            <Text size="12" className={`!leading-4 text-white  md:text-xs`}>
              {step.stepTitle}
            </Text>
          </Column>
        </Row>
      ))}
    </Row>
  )
}

type Option = {
  key: string
  value: string
}

type Question = {
  ask: string
  key: string
  options: Option[]
}

const questions: Question[] = [
  {
    ask: 'Atualmente você estuda em qual segmento?',
    key: 'SEGMENT',
    options: [
      {
        key: 'FUNDAMENTAL_1to5',
        value: 'Ensino Fundamental Anos Iniciais (1º a 5º ano)'
      },
      {
        key: 'FUNDAMENTAL_6to9',
        value: 'Ensino Fundamental Anos Finais (6º a 9º ano)'
      },
      {
        key: 'HIGH_SCHOOL',
        value: 'Ensino médio (1º a 3º ano)'
      }
    ]
  },
  {
    ask: 'Há quanto tempo você estuda com programa Bilíngue?',
    key: 'ENGLISH_STUDY_DURATION',
    options: [
      {
        key: 'NOT_STUDY',
        value: 'Não estudo com nenhum programa bilíngue'
      },
      {
        key: 'LESS_THAN_1_YEAR',
        value: 'Há menos de 1 ano'
      },
      {
        key: '1_YEAR',
        value: 'Há 1 ano'
      },
      {
        key: '2_YEARS',
        value: 'Há 2 anos'
      },
      {
        key: '3_YEARS',
        value: 'Há 3 anos'
      },
      {
        key: 'MORE_THAN_4_YEARS',
        value: 'Há mais de 4 anos'
      }
    ]
  },
  {
    ask: 'Você estuda atualmente em uma escola de inglês fora do colégio?',
    key: 'OUTSIDE_SCHOOL_ENGLISH_STUDY',
    options: [
      {
        key: 'YES',
        value: 'Sim'
      },
      {
        key: 'NO',
        value: 'Não'
      }
    ]
  },
  {
    ask: 'Há quanto tempo você estuda em uma escola de inglês fora do colégio?',
    key: 'OUTSIDE_SCHOOL_ENGLISH_STUDY_DURATION',
    options: [
      {
        key: 'LESS_THAN_1_YEAR',
        value: 'Há menos de 1 ano'
      },
      {
        key: '1_YEAR',
        value: 'Há 1 ano'
      },
      {
        key: '2_YEARS',
        value: 'Há 2 anos'
      },
      {
        key: '3_YEARS',
        value: 'Há 3 anos'
      },
      {
        key: 'MORE_THAN_4_YEARS',
        value: 'Há mais de 4 anos'
      },
      {
        key: 'NO_OUTSIDE_ENGLISH_STUDY',
        value: 'Não estudo inglês fora.'
      }
    ]
  },
  {
    ask: 'Como você avalia sua participação em sala de aula?',
    key: 'CLASS_PARTICIPATION',
    options: [
      {
        key: 'NONE',
        value: 'Não participo das atividades'
      },
      {
        key: 'LITTLE',
        value: 'Participo pouco das atividades'
      },
      {
        key: 'MOST_OF_THE_TIME',
        value: 'Participo das atividades na maior parte do tempo'
      },
      {
        key: 'A_LOT',
        value: 'Participo muito das atividades'
      }
    ]
  },
  {
    ask: 'Como você avalia sua compreensão oral?',
    key: 'ORAL_COMPREHENSION',
    options: [
      {
        key: 'NOTHING',
        value: 'Não entendo nada que o/a professor(a) diz'
      },
      {
        key: 'LITTLE',
        value: 'Entendo pouca coisa do que o/a professor(a) diz'
      },
      {
        key: 'GOOD_PART',
        value: 'Entendo boa parte do que o/a professor(a) diz'
      },
      {
        key: 'ALMOST_EVERYTHING',
        value: 'Entendo tudo ou quase tudo que o/a professor(a) diz'
      }
    ]
  },
  {
    ask: 'Como você avalia sua expressão oral?',
    key: 'ORAL_EXPRESSION',
    options: [
      {
        key: 'NEVER',
        value: 'Nunca falo inglês na sala'
      },
      {
        key: 'LITTLE',
        value: 'Falo pouco inglês na sala'
      },
      {
        key: 'TRY_TO_SPEAK',
        value: 'Tento falar inglês sempre que o/a professor(a) chama'
      },
      {
        key: 'ALWAYS',
        value: 'Sempre falo inglês em sala até com os meus colegas'
      }
    ]
  },
  {
    ask: 'Como você avalia a sua exposição ao inglês fora da sala?',
    key: 'ENGLISH_EXPOSURE_OUTSIDE_CLASS',
    options: [
      {
        key: 'NEVER_READ_WATCH',
        value: 'Nunca leio nada, nem assisto nada em inglês fora da sala'
      },
      {
        key: 'FEW_TIMES_READ_WATCH',
        value:
          'Poucas vezes leio ou assisto alguma coisa em inglês fora da sala'
      },
      {
        key: 'SOMETIMES_READ_WATCH',
        value: 'Às vezes leio ou assisto alguma coisa em inglês fora da sala'
      },
      {
        key: 'FREQUENTLY_READ_WATCH',
        value:
          'Com frequência leio ou assisto alguma coisa em inglês fora da sala'
      }
    ]
  }
]

type Answer = {
  question: string
  answer: string
}

export type FormType = {
  name: string
  birthDate: string
  SEGMENT: string
  ENGLISH_STUDY_DURATION: string
  OUTSIDE_SCHOOL_ENGLISH_STUDY: string
}

const OnboardingModal: React.FC = () => {
  const navigate = useNavigate()

  const [position, setPosition] = useState<number>(0)
  const [questionIndex, setQuestionIndex] = useState<number>(0)
  const [checkedIndex, setCheckedIndex] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const { student, resetTestData } = useAuth()

  const { handleSubmit, register, getError, setValue, getValues } =
    useForm<FormType>()

  const onSubmit = () => {
    setPosition(position + 1)
  }

  const [showWrongDataWidget, setShowWrongDataWidget] = useState<boolean>(false)

  const handleWrongData = useCallback(() => {
    setShowWrongDataWidget(true)
    setIsSubmitting(true)
    saveOnboardingQuestions({ isStudentDataInvalid: true }).finally(() => {
      setIsSubmitting(false)
    })
  }, [])

  const handleCheckInput = (
    checked: boolean,
    index: number,
    optionKey: string
  ) => {
    const newIndex = checked ? index : null
    setCheckedIndex(newIndex)
    setValue(questions[questionIndex].key as keyof FormType, optionKey)

    if (questionIndex + 1 === questions.length) {
      const values = getValues()
      const answers: Answer[] = Object.keys(values)
        .map((key, index) => ({
          question: key,
          answer: Object.values(values)[index]
        }))
        .filter(({ answer }) => !!answer)

      setIsSubmitting(true)
      saveOnboardingQuestions({ onboardingQuestions: answers })
        .then(() => {
          navigate(ROUTES.ONBOARDING.WELCOME)
        })
        .finally(() => {
          setIsSubmitting(false)
        })
    } else {
      if (
        questions[questionIndex].key === 'OUTSIDE_SCHOOL_ENGLISH_STUDY' &&
        optionKey === 'NO'
      ) {
        setTimeout(() => {
          setCheckedIndex(null)
          setQuestionIndex(questionIndex + 2)
        }, 500)
      } else {
        setTimeout(() => {
          setCheckedIndex(null)
          setQuestionIndex(questionIndex + 1)
        }, 500)
      }
    }
  }

  return (
    <Modal closable={false}>
      <Row className="h-full w-full flex-col items-center rounded-[4px] md:flex-row">
        <Row className="h-16 w-full items-center gap-4 rounded-t bg-[#9B5EFD] px-6 md:h-full md:w-1/4 md:flex-col md:items-baseline md:gap-14 md:rounded-t-none md:rounded-bl md:rounded-tl md:pb-8 md:pl-10 md:pt-12">
          <img
            src={IconLogo}
            alt="FluencyFlow"
            width={24}
            height={24}
            className="h-6 w-6 md:h-10 md:w-10"
          />
          <Row className="w-full flex-wrap">
            <Steps active={position} />
          </Row>
        </Row>
        <Column
          className={classNameBuilder(
            'relative h-full w-full rounded-b pt-[108px] md:w-3/4 md:justify-center md:rounded-r md:pt-0',
            showWrongDataWidget && 'justify-center md:pt-0'
          )}
        >
          {!showWrongDataWidget && (
            <>
              <Row className="w-full flex-wrap items-center border-b border-[#E0E0E0] pb-6 pl-4 pt-4 md:h-1/4 md:pl-14">
                <Column className="gap-2">
                  <Text.Translated
                    color="400"
                    size="12"
                    className="md:text-sm"
                    translationKey="step_number_onboarding_modal"
                    values={[position + 1]}
                  />
                  <Text color="600" size="lg" fontWeight="bold">
                    {steps[position].title}
                  </Text>
                  <Text.Translated
                    color="600"
                    className="!md:text-xs"
                    size="12"
                    translationKey="before_start_verification_onboarding_modal"
                  />
                </Column>
                {isSubmitting && (
                  <Row className="absolute right-4 top-4">
                    <CircularProgress size="sm" />
                  </Row>
                )}
              </Row>
              <Row className="h-full px-4 py-4 md:px-14 md:py-10">
                <Form onSubmit={handleSubmit(onSubmit)} className="w-full">
                  {position === 0 && (
                    <Column className="gap-y-11">
                      <Text.Translated
                        fontWeight="bold"
                        size="xl"
                        color="600"
                        translationKey="student_data_onboarding_modal"
                      />
                      <Row className="w-full flex-wrap gap-6 md:flex-nowrap md:gap-4">
                        <Column className="w-full md:w-3/5">
                          <TextInput
                            {...register('name', {
                              required,
                              value: student?.name
                            })}
                            label="Nome do aluno"
                            disabled
                            className={{
                              wrapper: 'text-sm font-medium text-[#282828]'
                            }}
                            error={getError('name')}
                          />
                        </Column>
                        <Column className="w-3/5 md:w-2/5">
                          <TextInput
                            {...register('birthDate', {
                              value: student?.birthDate
                                ? dayjs(student?.birthDate).format('DD/MM/YYYY')
                                : undefined
                            })}
                            label="Data de Nascimento"
                            disabled
                            error={getError('birthDate')}
                            className={{
                              wrapper: 'text-sm font-medium text-[#282828]'
                            }}
                          />
                        </Column>
                      </Row>
                      <Row className="w-full justify-end gap-2">
                        <Button.Translated
                          translationKey="incorrect_data_button_setup_states_welcome_onboarding_modal"
                          type="button"
                          size="md"
                          variant="outline"
                          className="w-full md:w-auto"
                          onClick={handleWrongData}
                        />
                        <Button.Translated
                          translationKey="confirm_data_button_setup_states_welcome_onboarding_modal"
                          type="submit"
                          color="flat-dark"
                          size="md"
                          className="w-full md:w-auto"
                        />
                      </Row>
                    </Column>
                  )}
                  {position === 1 && (
                    <Column className="h-full justify-between">
                      <Row
                        className={classNameBuilder(
                          'flex-wrap gap-10 transition-opacity duration-500 md:gap-6'
                        )}
                      >
                        <Row className="pr-3">
                          <Text
                            className="md:text-lg"
                            size="sm"
                            color="600"
                            fontWeight="bold"
                          >
                            {questions[questionIndex].ask}
                          </Text>
                        </Row>
                        <Row className="w-full flex-wrap gap-4">
                          {questions[questionIndex].options.map(
                            (option, index) => (
                              <Row
                                key={index}
                                className={classNameBuilder(
                                  'relative z-10 w-full transition-all duration-500'
                                )}
                              >
                                <CheckboxInput
                                  direction="row"
                                  className={{
                                    wrapper: '!flex-row-reverse',
                                    label: '!text-gray-600'
                                  }}
                                  key={index}
                                  {...register(
                                    questions[questionIndex]
                                      .key as keyof FormType
                                  )}
                                  checked={index === checkedIndex}
                                  onChange={(event) =>
                                    handleCheckInput(
                                      event.target.checked,
                                      index,
                                      option.key
                                    )
                                  }
                                  label={option.value}
                                />
                              </Row>
                            )
                          )}
                        </Row>
                      </Row>
                      <ProgressBar
                        progress={(1 / questions.length) * (questionIndex + 1)}
                        className={{
                          wrapper: 'h-1 bg-[#E0E0E0]',
                          divider: false,
                          bar: 'bg-[#6A0DFD]'
                        }}
                      />
                    </Column>
                  )}
                </Form>
              </Row>
            </>
          )}
          {showWrongDataWidget && (
            <Row className="w-full flex-wrap justify-center gap-4">
              <Row className="w-full items-center justify-center">
                <div className="flex h-[120px] w-[120px] items-center justify-center rounded-[100%] bg-[#F90303] bg-opacity-20">
                  <img
                    src={ExclamationIcon}
                    alt="error"
                    width={48}
                    height={48}
                  />
                </div>
              </Row>
              <Row className="w-2/4 flex-col items-center justify-center gap-4">
                <Text.Translated
                  size="lg"
                  fontWeight="bold"
                  color="600"
                  translationKey="incorrect_registration_onboarding_modal"
                />
                <Text.Translated
                  size="xs"
                  color="600"
                  align="center"
                  translationKey="cannot_continue_test_onboarding_modal"
                />
              </Row>
              <Row className="mt-[32px] w-full items-center justify-center">
                <Button.Translated
                  size="md"
                  translationKey="understood_button_setup_states_welcome_onboarding_modal"
                  type="button"
                  onClick={() => {
                    resetTestData()
                    localStorage.removeItem('accessToken')
                    window.location.reload()
                  }}
                />
              </Row>
            </Row>
          )}
        </Column>
      </Row>
    </Modal>
  )
}

export default OnboardingModal
